import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { LoaderAction, AreasAction } from "redux/actions";

import { errorHandler } from "api/api/errorHandler";
import { isValidInputFields } from "features/shipping_methods/helpers";
import useGetStoreAddedMappedAreas from "./hooks/useGetStoreAddedMapAreas";

import { API } from "api";
import { UTILS } from "utils";
import { errorNotification } from "utils/Utils";
import { calculateDeliveryTimeInMins } from "../../helpers";
import AppInput from "components/ui/AppInput";
import AppSelect from "components/ui/AppSelect";
import AppButton from "components/ui/AppButton";
import AppOutlineButton from "components/ui/AppOutlineButton";
import AppModal from "components/ui/AppModal";


const UpdateAreas = ({ open, onClose, selectedAreas, setSelectedRowsKeys }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const getStoreMappedAddedAreas = useGetStoreAddedMappedAreas();

  const { isLoading } = useSelector((state) => state.LoaderReducer);
  const normalizedSelectedAreas = useMemo(() => {
    if (Array.isArray(selectedAreas)) return selectedAreas;
    if (selectedAreas && typeof selectedAreas === "object") return [selectedAreas];
    return [];
  }, [selectedAreas]);

  const [state, setState] = useState({
    minOrder: "",
    deliveryFee: "",
    deliveryTime: {
      Hours: 0,
      Minutes: 0,
      Days: 0,
    },
  });
  const handleValues = (key, val) => setState({ ...state, [key]: val });

  useEffect(() => {
    if (!open) return;
    const area = normalizedSelectedAreas?.[0];
    if (!area) return;
    const mins = Number(area?.deliveryTime || 0);
    setState({
      minOrder: area?.minOrder ?? "",
      deliveryFee: area?.deliveryFee ?? "",
      deliveryTime: {
        Days: Math.floor(mins / 1440),
        Hours: Math.floor((mins % 1440) / 60),
        Minutes: Math.floor((mins % 1440) % 60),
      },
    });
  }, [open, normalizedSelectedAreas]);

  const editAreas = async () => {
    try {
      const { error, isValid } = isValidInputFields({
        isLoading,
        state,
        selectedAreas: normalizedSelectedAreas,
      });
      if (!isValid) {
        errorNotification(error);
        return;
      }
      dispatch(LoaderAction.setLoading(true));
      const deliveryTimeInMins = calculateDeliveryTimeInMins({
        deliveryTime: state.deliveryTime,
      });
      const userDetail = UTILS.getCurrentUser();
      let objectArray = [];
      normalizedSelectedAreas.forEach((area) => {
        objectArray.push(area._id);
      });

      const result = await API.updateStoreMappedAreas({
        brandId: userDetail.brandId,
        storeId: normalizedSelectedAreas[0].storeId,
        mappedAreaBaseDeliveryMethodIds: objectArray,
        minOrder: Number(state.minOrder),
        deliveryFee: Number(state.deliveryFee),
        deliveryTime: Number(deliveryTimeInMins),
      });
      if (result?.success === 1) {
        UTILS.successNotification(t(result.message));
        dispatch(AreasAction.setNewMappedAreas([]));
      } else {
        UTILS.errorNotification(errorHandler(result));
      }
      if (typeof setSelectedRowsKeys === "function") setSelectedRowsKeys([]);
      await getStoreMappedAddedAreas({
        storeId: normalizedSelectedAreas[0].storeId,
      });
      onClose();
    } catch (error) {
      UTILS.errorNotification(errorHandler(error));
    } finally {
      dispatch(LoaderAction.setLoading(false));
    }
  };

  const handleInputValue = (key, val) =>
    handleValues(key, UTILS.NumberConversion(val));

  const daysOptions = Array.from({ length: 8 }, (_, value) => ({
    label: `${value} ${t("Days")}`,
    value,
  }));
  const hourOptions = Array.from({ length: 24 }, (_, value) => ({
    label: `${value} ${t("Hours")}`,
    value,
  }));
  const minuteOptions = Array.from({ length: 12 }, (_, index) => ({
    label: `${index * 5} ${t("Minutes")}`,
    value: index * 5,
  }));

  const footer = (
    <div className="mt-4 flex justify-end gap-2">
      <AppOutlineButton variant="secondary" onClick={onClose} disabled={isLoading}>
        {t("Cancel")}
      </AppOutlineButton>
      <AppButton variant="primary" onClick={editAreas} loading={isLoading}>
        {t("Save Changes")}
      </AppButton>
    </div>
  );
  return (
    <AppModal
      open={open}
      onClose={onClose}
      width={620}
      title={t("Edit delivery areas")}
      footer={footer}
    >
    <div className="w-full">
      <div className="grid grid-cols-2 gap-4">
        <AppInput
          label={t("Min. Basket Value")}
          value={state.minOrder}
          onInput={(e) => handleInputValue("minOrder", e.target.value)}
        />
        <AppInput
          label={t("Delivery Fee")}
          value={state.deliveryFee}
          onInput={(e) => handleInputValue("deliveryFee", e.target.value)}
        />
      </div>
      <div className="my-3 text-lg font-bold">{t("Delivery Time")}</div>
      <div className="grid grid-cols-3 gap-4">
        <AppSelect
          placeholder={t("Select Days")}
          value={state.deliveryTime.Days}
          options={daysOptions}
          onChange={(value) =>
            handleValues("deliveryTime", {
              ...state.deliveryTime,
              Days: Number(value || 0),
            })
          }
        />
        <AppSelect
          placeholder={t("Select Hours")}
          value={state.deliveryTime.Hours}
          options={hourOptions}
          onChange={(value) =>
            handleValues("deliveryTime", {
              ...state.deliveryTime,
              Hours: Number(value || 0),
            })
          }
        />
        <AppSelect
          placeholder={t("Select Minutes")}
          value={state.deliveryTime.Minutes}
          options={minuteOptions}
          onChange={(value) =>
            handleValues("deliveryTime", {
              ...state.deliveryTime,
              Minutes: Number(value || 0),
            })
          }
        />
      </div>
    </div>
    </AppModal>
  );
};

UpdateAreas.propTypes = {
  onClose: PropTypes.func,
};

export default UpdateAreas;
